<template>
  <li
    class="ai-memo"
    :class="[`is-${item.status}`, { 'is-editing': editing }]"
  >
    <template v-if="editing">
      <textarea
        ref="text"
        v-model="text"
        class="ai-input"
        rows="3"
        :maxlength="maxText"
        aria-label="Текст записи"
      />
      <div class="ai-memo__row">
        <input
          v-model="subject"
          class="ai-input"
          :maxlength="maxSubject"
          placeholder="Тема (необязательно)"
          aria-label="Тема"
        >
        <span class="ai-memo__counter">{{ text.length }}/{{ maxText }}</span>
      </div>
      <div class="ai-memo__edit-actions">
        <button
          type="button"
          class="ai-button is-small"
          :disabled="busy"
          @click="editing = false"
        >
          Отмена
        </button>
        <button
          type="button"
          class="ai-button is-small is-primary"
          :disabled="busy || !text.trim() || !changed"
          @click="save"
        >
          <span
            v-if="busy"
            class="ai-spinner ai-spinner--small"
          />
          {{ busy ? 'Проверяю…' : 'Сохранить' }}
        </button>
      </div>
    </template>

    <template v-else>
      <span
        v-if="item.subject"
        class="ai-memo__subject"
      >{{ item.subject }}</span>
      <p
        class="ai-memo__text"
        v-text="item.text"
      />
      <p
        v-if="conflict"
        class="ai-memo__conflict ai-tone is-warning"
      >
        <ai-icon
          name="alert"
          :size="14"
        />
        <span>{{ conflictText }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="ai-link-button"
          :disabled="busy"
          @click="act('resolve')"
        >
          Решить
        </button>
      </p>
      <div class="ai-memo__footer">
        <span class="ai-memo__meta">
          <span
            v-if="statusLabel"
            class="ai-memo__status"
          >{{ statusLabel }}</span>
          <span
            v-for="(part, index) in meta"
            :key="index"
            class="ai-memo__part"
          >{{ part }}</span>
        </span>
        <confirm-inline
          v-if="canEdit && confirming"
          class="ai-memo__actions"
          text="Удалить запись из памяти?"
          :busy="busy"
          @confirm="remove"
          @cancel="confirming = false"
        />
        <span
          v-else-if="canEdit"
          class="ai-memo__actions"
        >
          <span
            v-if="busy"
            class="ai-spinner ai-spinner--small"
          />
          <template v-if="item.status === 'pending'">
            <button
              type="button"
              class="ai-button is-small is-primary"
              :disabled="busy"
              @click="act('approve')"
            >
              Сохранить
            </button>
            <button
              type="button"
              class="ai-button is-small"
              :disabled="busy"
              @click="act('reject')"
            >
              Отклонить
            </button>
          </template>
          <button
            v-if="item.status === 'active' || item.status === 'pending'"
            type="button"
            class="ai-link-button"
            :disabled="busy"
            @click="startEdit"
          >
            Изменить
          </button>
          <button
            v-if="item.status === 'active'"
            type="button"
            class="ai-link-button"
            :disabled="busy"
            @click="act('archive')"
          >
            В архив
          </button>
          <button
            v-if="item.status === 'archived' || item.status === 'rejected'"
            type="button"
            class="ai-link-button"
            :disabled="busy"
            @click="act('restore')"
          >
            Вернуть
          </button>
          <button
            v-if="item.status !== 'deleted'"
            type="button"
            class="ai-icon-button is-small"
            title="Удалить"
            :disabled="busy"
            @click="confirming = true"
          >
            <ai-icon
              name="trash"
              :size="14"
            />
          </button>
        </span>
      </div>
    </template>
  </li>
</template>

<script>
  import AiIcon from './AiIcon.vue';
  import ConfirmInline from './ConfirmInline.vue';
  import { MAX_SUBJECT, MAX_TEXT, VERDICTS, hasConflict, memoryMeta } from './memory';

  const STATUSES = {
    pending: 'предложение',
    archived: 'в архиве',
    rejected: 'отклонено',
    deleted: 'удалено',
  };

  // One memory entry: text, author line, and for administrators inline editing and status actions.
  // `run(item, action, payload)` does the change (MemoryPanel.act) and resolves to true on success.
  export default {
    name: 'MemoryItem',
    components: { AiIcon, ConfirmInline },
    props: {
      item: { type: Object, required: true },
      canEdit: { type: Boolean, default: false },
      run: { type: Function, required: true },
      // Show the status (in check results, where entries of any status meet).
      showStatus: { type: Boolean, default: false },
    },
    data: () => ({
      editing: false,
      text: '',
      subject: '',
      busy: false,
      // Deletion is being confirmed (in place of the actions).
      confirming: false,
      maxText: MAX_TEXT,
      maxSubject: MAX_SUBJECT,
    }),
    computed: {
      me() {
        return this.$store.getters['user/userId'];
      },
      meta() {
        return memoryMeta(this.item, this.me);
      },
      conflict() {
        return hasConflict(this.item);
      },
      conflictText() {
        const verdict = VERDICTS[this.item.conflict.verdict] || 'расходится с';
        return `${verdict[0].toUpperCase()}${verdict.slice(1)} записи «${this.item.conflict.text}»`;
      },
      statusLabel() {
        if (!this.showStatus && this.item.status !== 'deleted') return '';
        return STATUSES[this.item.status] || '';
      },
      changed() {
        return this.text.trim() !== this.item.text || this.subject.trim() !== (this.item.subject || '');
      },
    },
    methods: {
      startEdit() {
        this.text = this.item.text;
        this.subject = this.item.subject || '';
        this.editing = true;
        this.$nextTick(() => this.$refs.text && this.$refs.text.focus());
      },
      async act(action, payload) {
        this.busy = true;
        try {
          return await this.run(this.item, action, payload);
        } finally {
          this.busy = false;
        }
      },
      async remove() {
        await this.act('remove');
        this.confirming = false;
      },
      async save() {
        const ok = await this.act('save', { text: this.text.trim(), subject: this.subject.trim() });
        if (ok) this.editing = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-memo {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface);

    &.is-pending {
      border-left: 3px solid var(--ui-warning);
    }

    &.is-archived,
    &.is-rejected,
    &.is-deleted {
      background: var(--ui-surface-soft);

      .ai-memo__text {
        color: var(--ui-text-muted);
      }
    }

    &.is-editing {
      gap: 8px;
      border-color: var(--ui-primary);
    }
  }

  .ai-memo__subject {
    align-self: flex-start;
    max-width: 100%;
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--ui-bg-strong);
    color: var(--ui-text-muted);
    font-size: 11px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-memo__text {
    margin: 0;
    color: var(--ui-text);
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .ai-memo__conflict {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 2px 0 0;
    padding: 6px 8px;
    border-radius: var(--ui-radius-sm);
    font-size: 12px;

    span {
      flex: 1 1 200px;
      overflow-wrap: anywhere;
    }
  }

  .ai-memo__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 12px;
    min-height: 26px;
  }

  .ai-memo__meta {
    flex: 1 1 180px;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  // Parts wrap between each other, not inside a date; the separator stays at the end of a line.
  .ai-memo__part {
    display: inline-block;
    white-space: nowrap;

    &:not(:last-child)::after {
      content: '·';
      margin: 0 5px;
    }
  }

  .ai-memo__status {
    margin-right: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--ui-bg-strong);
    color: var(--ui-text);
    font-size: 11px;
  }

  .ai-memo__actions {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 4px 12px;
    margin-left: auto;

    .ai-link-button {
      font-size: 12px;
    }

    .ai-button + .ai-button {
      margin-left: -6px;
    }
  }

  .ai-memo__row {
    display: flex;
    align-items: center;
    gap: 10px;

    .ai-input {
      flex: 1;
      min-width: 0;
    }
  }

  .ai-memo__counter {
    color: var(--ui-text-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  .ai-memo__edit-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
</style>
